/**
 * QR Code Block - View Script
 *
 * Generates the QR SVG from data attributes and inserts it into the DOM.
 * Also handles the optional download button (progressive enhancement).
 * Uses qrcode-generator — pure synchronous JS, no React dependency.
 */

import qrcode from 'qrcode-generator';

/**
 * Build an SVG string for the given QR matrix.
 *
 * @param {Object} qr        qrcode-generator instance (after make()).
 * @param {number} size      Desired output size in px.
 * @param {number} margin    Quiet zone in cells.
 * @param {string} fg        Foreground color.
 * @param {string} bg        Background color.
 * @return {string} SVG markup.
 */
function buildSvg( qr, size, margin, fg, bg ) {
	const count = qr.getModuleCount();
	const cell = size / ( count + 2 * margin );

	const rects = [];
	for ( let row = 0; row < count; row++ ) {
		for ( let col = 0; col < count; col++ ) {
			if ( qr.isDark( row, col ) ) {
				const x = ( col + margin ) * cell;
				const y = ( row + margin ) * cell;
				rects.push(
					`<rect x="${ x }" y="${ y }" width="${ cell }" height="${ cell }" fill="${ fg }"/>`
				);
			}
		}
	}

	return [
		`<svg xmlns="http://www.w3.org/2000/svg" width="${ size }" height="${ size }" viewBox="0 0 ${ size } ${ size }">`,
		`<rect width="${ size }" height="${ size }" fill="${ bg }"/>`,
		...rects,
		'</svg>',
	].join( '' );
}

document.addEventListener( 'DOMContentLoaded', () => {
	document.querySelectorAll( '.brandy-qr[data-qr-content]' ).forEach( ( root ) => {
		const content = root.dataset.qrContent;
		if ( ! content ) return;

		const size = parseInt( root.dataset.qrSize, 10 ) || 200;
		const fg = root.dataset.qrFg || '#000000';
		const bg = root.dataset.qrBg || '#ffffff';
		const margin = parseInt( root.dataset.qrMargin, 10 );
		// qrcode-generator uses cells; clamp to 0 when NaN.
		const marginCells = Number.isNaN( margin ) ? 1 : margin;

		// Generate QR matrix — type number 0 = auto, level M = ~15% error correction.
		const qr = qrcode( 0, 'M' );
		qr.addData( content );
		qr.make();

		const svg = buildSvg( qr, size, marginCells, fg, bg );

		// Insert SVG before caption/download button.
		root.insertAdjacentHTML( 'afterbegin', svg );

		// Wire download button if present.
		const btn = root.querySelector( '.brandy-qr__download' );
		if ( btn ) {
			root.classList.add( 'js-qr-ready' );
			btn.addEventListener( 'click', () => {
				const blob = new Blob( [ svg ], { type: 'image/svg+xml' } );
				const url = URL.createObjectURL( blob );
				const a = document.createElement( 'a' );
				a.href = url;
				a.download = 'qrcode.svg';
				// Anchor must be in DOM for Firefox to trigger file download.
				document.body.appendChild( a );
				a.click();
				document.body.removeChild( a );
				URL.revokeObjectURL( url );
			} );
		}
	} );
} );
